import { useState } from "react";
import { Stack, Text, VStack } from "@chakra-ui/react";
import Joi from "joi";

import { DISPOSITIONS, IDispositionItemConfig } from "@ud/config/dispositions";
import FormChoice from "components/Chakra/FormChoice";
import FormRange from "components/Chakra/FormRange";
import Button from "components/Chakra/Button";
import { NewWDType } from "scenes/Watchdog/types";
import NumericInterval from "utils/NumericInterval";
import { dispositionsOptions } from "utils/search/types";
import useForm, { OnFormSubmitType } from "hooks/useForm";

interface IFormValues {
  min: number;
  max: number;
}

const formSchema = Joi.object({
  min: Joi.number().optional().integer().label("od").allow(""),
  max: Joi.number()
    .optional()
    .integer()
    .when("min", {
      is: Joi.string().empty(""),
      then: Joi.number().min(0),
      otherwise: Joi.number().min(Joi.ref("min", { render: true })),
    })
    .allow(""),
});

interface IProps {
  setWatchdogStep: React.Dispatch<React.SetStateAction<NewWDType>>;
}

const LAST_STEP = 3;

const Step2Form: React.FC<IProps> = ({ setWatchdogStep }): JSX.Element => {
  const [dispositions, setDispositions] = useState<Array<string>>([]);

  const { handleSubmit, registerWithError } = useForm<IFormValues>(formSchema);

  const handleData: OnFormSubmitType<IFormValues> = (
    data: IFormValues,
  ): void => {
    const { min, max } = data;
    setWatchdogStep((prev) => ({
      ...prev,
      dispositions: dispositions
        .map((disposition) =>
          DISPOSITIONS.find((d) => d.queryValue === disposition),
        )
        .filter(Boolean) as Array<IDispositionItemConfig>,
      step: 3,
      price: new NumericInterval(min, max),
      maxLevel: prev.maxLevel === prev.step ? LAST_STEP : prev.maxLevel,
    }));
  };

  return (
    <VStack
      data-test="watchDogModalComponent"
      as="form"
      onSubmit={handleSubmit(handleData)}
      spacing={4}
      background="ud-white"
      borderRadius={6}
      boxShadow="0 0 10px 0 rgba(0, 0, 0, 0.19)"
      paddingY={12}
      paddingX={{ base: 5, sm: 12 }}
    >
      <Stack spacing={4} pt={4}>
        <Text variant="small_1" textTransform="uppercase" fontWeight="500">
          Dispozice
        </Text>
        <FormChoice
          dataTest="watchdog.filter.dispositions"
          options={dispositionsOptions}
          values={dispositions}
          onChange={(value) => {
            setDispositions((prevState) => {
              if (prevState.includes(value)) {
                return prevState.filter((disposition) => disposition !== value);
              }

              return [value, ...prevState];
            });
          }}
        />
      </Stack>
      <Stack spacing={4} pt={4} width="100%">
        <Text variant="small_1" textTransform="uppercase" fontWeight="500">
          Cena
        </Text>
        <FormRange
          dataTest="watchdog.filter.price"
          minInputProps={{ ...registerWithError("min") }}
          maxInputProps={{ ...registerWithError("max") }}
        />
      </Stack>

      <Button
        data-test="saveAndContinueButton"
        type="submit"
        width="max-content"
        trackEventPayload={{
          "dl.eCat": "landing",
          "dl.eAct": "watchdog.clicked",
          "dl.eLab": "WD 2. krok parametry",
        }}
      >
        Uložit a pokračovat
      </Button>
    </VStack>
  );
};

export default Step2Form;
